import React, { useEffect, useRef, useState, useContext } from 'react';
import ReactDOM from 'react-dom';
import { MapBrowserEvent, Overlay } from 'ol';
import Icon from 'components/Icon';
import Point from 'ol/geom/Point';
import VectorSource from 'ol/source/Vector';
import VectorLayer from 'ol/layer/Vector';
import Feature from 'ol/Feature';
import { Circle, Style } from 'ol/style';
import MapContext from 'components/Map/MapContext';
import { useMediaQuery } from 'react-responsive';
import { MapArea } from 'types/mapTypes/MapModel';
import clsx from 'clsx';

/*  React kan hantera overlay-element när det är enkelt, men när vi ska uppdatera listor och göra mer avancerade ändringar
    blir det problem eftersom både React och OpenLayers försöker styra samma DOM. 
    Därför skapas DOM manuellt här så att OpenLayers får full kontroll och vi undviker konflikter som leder till buggar.
*/

interface Props {
  areaForecasts: MapArea[];
  zoomLevel: number;
  subAreaZoomLevel: number;
}

function renderReactElementToDom(element: React.ReactElement): HTMLElement {
  const container = document.createElement('span');
  ReactDOM.render(element, container);
  return container;
}


const ForecastLayer: React.FC<Props> = ({ areaForecasts, zoomLevel, subAreaZoomLevel }) => {
  const { map } = useContext(MapContext);
  const [currentZoom, setCurrentZoom] = useState<number>(zoomLevel);
  const overlayRefs = useRef<Overlay[]>([]);
  const isMobileOrTablet = useMediaQuery({ maxWidth: 1023 });
  const isSmallMobile = useMediaQuery({ maxWidth: 380 });

  const Y_OFFSET = 10000;
  const Y_ZOOM_LEVEL = 6;
  const SUBAREA_ZOOM_LEVEL = subAreaZoomLevel ? subAreaZoomLevel : 6;
  let existingCoordinates: Point[] = [];

  const getMinYDistance = (isMobile: boolean, zoom: number): number => {
    const ranges = {
      mobile: [
        { min: 4, max: 4.7, distance: isSmallMobile ? 300000 : 220000 },
        { min: 4.7, max: 4.9, distance: 185000 },
        { min: 4.9, max: 5.2, distance: 160000 },
        { min: 5.2, max: 5.4, distance: 140000 },
        { min: 5.3, max: 5.6, distance: 120000 },
        { min: 5.6, max: 6, distance: 100000 },
      ],
      desktop: [
        { min: 4, max: 4.7, distance: 180000 },
        { min: 4.7, max: 5, distance: 185000 },
        { min: 5, max: 5.2, distance: 150000 },
        { min: 5.2, max: 5.4, distance: 130000 },
        { min: 5.4, max: 5.6, distance: 110000 },
        { min: 5.6, max: 6, distance: 100000 },
      ],
    };

    const selected = isMobile ? ranges.mobile : ranges.desktop;
    for (const range of selected) {
      if (zoom >= range.min && zoom <= range.max) return range.distance;
    }
    return 200000;
  };

  const adjustYCoordinate = (
    newCoordinate: Point,
    existingCoordinates: Point[],
    zoom: number
  ): Point => {
    let [newX, newY] = newCoordinate.getCoordinates();
    let adjustmentFactor = 0;
    let minDistance = getMinYDistance(isMobileOrTablet, zoom);

    while (
			existingCoordinates.some(
				existingPoint => 
					Math.abs(newY - existingPoint.getCoordinates()[1]) < minDistance
			)
		) {
      adjustmentFactor++;
      newY = newCoordinate.getCoordinates()[1] + Y_OFFSET * adjustmentFactor; // Increment Y coordinate
    }

    const adjustedPoint = new Point([newX, newY]);
    existingCoordinates.push(adjustedPoint);
    return adjustedPoint;
  };

  useEffect(() => {
    if (!map) {
			return;
		}

    setCurrentZoom(map.getView().getZoom() ?? zoomLevel);
  }, [map]);

  useEffect(() => {
    if (!map) return;

    const handler = () => {
      const zoom = map.getView().getZoom();
      if (zoom !== currentZoom) {
				setCurrentZoom(zoom ?? zoomLevel);
			}
    };

    map.on('moveend', handler);
    return () => {
			map.un('moveend', handler);
		}
  }, [map, currentZoom]);

  useEffect(() => {
    if (!map || !areaForecasts || areaForecasts.length === 0) {
			return;
		}

    /* Rensa gamla overlays */
    overlayRefs.current.forEach(overlayRef => {
      map.removeOverlay(overlayRef);
      overlayRef.getElement()?.remove();
    });
    overlayRefs.current = [];
    existingCoordinates = [];

    const renderedForecasts =
      currentZoom >= SUBAREA_ZOOM_LEVEL
        ? areaForecasts.flatMap(area =>
            area.areaMarkers?.length
              ? area.areaMarkers.map(sub => ({ ...sub, parentArea: area }))
              : [area]
          )
        : areaForecasts;

    const vectorSource = new VectorSource();
    const vectorLayer = new VectorLayer({
      source: vectorSource,
      zIndex: 22,
    });

    renderedForecasts.forEach((area, index) => {
      const coords = area.coordinates;
      const point = new Point(coords);
      const adjustedPoint = currentZoom < Y_ZOOM_LEVEL
        ? adjustYCoordinate(
					point,
					existingCoordinates,
					currentZoom
				) : point;

      /* Skapa DOM-element */
      const a = document.createElement('a');
      a.className = clsx(
        'relative w-full rounded-md border border-grey-border group bg-white hover:underline flex'
      );
      a.href = area.url;
      a.id = `marker${index}`;

      /* Ikoncontainer */
      const iconContainer = document.createElement('div');
      iconContainer.className = clsx(
        'bg-white rounded-md h-8 p-0 m-0 transition ease-in-out duration-75 bottom-0 border-l border-solid flex items-center'
      );

      if ('areaMarkers' in area && area.areaMarkers?.length) {
        area.areaMarkers.forEach((sub, subIdx) => {
          const img = document.createElement('img');
          img.src = sub.icon.src;
          img.alt = sub.icon.alt ?? '';
          img.width = 32;
          img.height = 32;
          img.className = clsx(
            subIdx !== 0 ? '-ml-6' : '',
            subIdx === 0 ? 'relative z-10' : 'relative z-0'
          );
          iconContainer.appendChild(img);
        });
      } else {
        const img = document.createElement('img');
        img.src = area.icon.src;
        img.alt = area.icon.alt ?? '';
        img.width = 32;
        img.height = 32;
        iconContainer.appendChild(img);
      }

      a.appendChild(iconContainer);

      /* Text med pil */
      const textDiv = document.createElement('div');
      textDiv.className = clsx(
        'h-8 pr-2 text-small font-semibold no-underline leading-8 whitespace-nowrap flex items-center'
      );
      textDiv.textContent = area.name;
      const span = document.createElement('span');
      const icon = renderReactElementToDom(
        <Icon icon="chevronSolid" direction="right" size={99} />
      );

      span.appendChild(icon)
      textDiv.appendChild(span);
      a.appendChild(textDiv);

      /* Skapa overlay */
      const overlay = new Overlay({
        id: `marker${index}`,
        element: a,
        position: adjustedPoint.getCoordinates(),
        offset: [5, -17],
        stopEvent: false,
      });
			
      overlayRefs.current.push(overlay);
      map.addOverlay(overlay);

      /* Lägg till eventuell feature */
      const circleFeature = new Feature({
				geometry: point,
				name: area.name,
			});
      circleFeature.setStyle(
				new Style({
					image: new Circle({
						radius: 15,
					}),
				})
			);
      vectorSource.addFeatures([circleFeature]);
    });

    map.addLayer(vectorLayer);

    const hover = (e: MapBrowserEvent<UIEvent>) => {
      if (e.dragging) return;
      const hit = map.hasFeatureAtPixel(map.getEventPixel(e.originalEvent));
      map.getTargetElement().style.cursor = hit ? 'pointer' : '';
    };

    map.on('pointermove', hover);

    return () => {
      overlayRefs.current.forEach(overlayRef => {
        map.removeOverlay(overlayRef);
        overlayRef.getElement()?.remove();
      });
      overlayRefs.current = [];
      map.removeLayer(vectorLayer);
      map.un('pointermove', hover);
    };
  }, [map, areaForecasts, currentZoom]);

  return null;
};

export default ForecastLayer;
